import { IState } from "../types/state";

/**
 * Executor - manages a stack of states/modules
 * Reference: quadra/source/overmind.h:33-51
 */
export default class Executor {
  private states: IState[] = [];
  public paused: boolean = false;
  public selfDestruct: boolean = false;
  public done: boolean = false;

  /**
   * Constructor
   * @param selfDestruct If true, executor will be cleaned up when done
   * Reference: quadra/source/overmind.cc:91-94
   */
  constructor(selfDestruct: boolean = false) {
    this.done = false;
    this.paused = false;
    this.selfDestruct = selfDestruct;
  }

  /**
   * Add a state to the executor
   * Reference: quadra/source/overmind.cc:126-129
   */
  add(state: IState): void {
    this.states.push(state);
    // Set parent executor on the state (matches original m->parent=this)
    if (state.setParentExecutor) {
      state.setParentExecutor(this);
    }
  }

  /**
   * Remove the top state from the executor
   * Reference: quadra/source/overmind.cc:101-104
   */
  remove(): void {
    if (this.states.length > 0) {
      const state = this.states.pop();
      // Call destructor if the state has one (matches original C++ destructor behavior)
      if (state && 'destructor' in state && typeof (state as any).destructor === 'function') {
        (state as any).destructor();
      }
    }
  }

  /**
   * Step the executor (process top state)
   * Reference: quadra/source/overmind.cc:106-124
   */
  step(): void {
    if (this.paused) {
      return;
    }
    
    if (this.states.length > 0) {
      const topState = this.states[this.states.length - 1];
      
      if (!topState.isDone || !topState.isDone()) {
        // Check if first time
        if (topState.isFirstTime && topState.isFirstTime()) {
          topState.enter();
          // Mark as no longer first time
          // Check if it's a GameState instance which has markFirstTimeComplete
          if ('markFirstTimeComplete' in topState && typeof (topState as any).markFirstTimeComplete === 'function') {
            (topState as any).markFirstTimeComplete();
          }
        } else {
          const frameTime = 10; // Match game step interval (100fps = 10ms per step)
          topState.update(frameTime);
        }
      }
      
      // Remove all done states from top
      while (this.states.length > 0) {
        const top = this.states[this.states.length - 1];
        if (top.isDone && top.isDone()) {
          this.remove();
        } else {
          break;
        }
      }
    }
    
    if (this.states.length === 0) {
      this.done = true;
    }
  }

  /**
   * Pause this executor
   * Reference: quadra/source/overmind.h:44-46
   */
  pause(): void {
    this.paused = true;
  }

  /**
   * Unpause this executor
   * Reference: quadra/source/overmind.h:47-49
   */
  unpause(): void {
    this.paused = false;
  }

  /**
   * Get the current top state
   */
  getTopState(): IState | undefined {
    return this.states.length > 0 ? this.states[this.states.length - 1] : undefined;
  }
}

